import { createHashRouter, Navigate, type RouteObject } from 'react-router-dom';
import { AppShell } from '@/components/AppShell';
import { RequirePermission } from '@/components/RequirePermission';
import { NotBuiltYet } from '@/components/states';
import { HomePage } from '@/workspaces/home/HomePage';
import { SchoolSalesPage } from '@/workspaces/crm/SchoolSalesPage';
import { TransactionsPage } from '@/workspaces/crm/TransactionsPage';
import { DishCatalogPage } from '@/workspaces/restaurants/DishCatalogPage';
import { RestaurantSalesPage } from '@/workspaces/restaurants/RestaurantSalesPage';
import { DishSalesPage } from '@/workspaces/restaurants/DishSalesPage';
import { RestaurantSchoolsPage } from '@/workspaces/restaurants/RestaurantSchoolsPage';
import { DriveOverviewPage } from '@/workspaces/drive/DriveOverviewPage';
import { DriveSchoolsPage } from '@/workspaces/drive/DriveSchoolsPage';
import { DriveHistoryPage } from '@/workspaces/drive/DriveHistoryPage';
import { InsightsPage } from '@/workspaces/insights/InsightsPage';
import { PowiazaniaPage } from '@/workspaces/insights/PowiazaniaPage';
import { SystemStatusPage } from '@/workspaces/system/SystemStatusPage';
import { GotowoscPage } from '@/workspaces/system/GotowoscPage';
import { JakoscDanychPage } from '@/workspaces/system/JakoscDanychPage';
import { WORKSPACES, findWorkspace, sectionPath, type WorkspaceSection } from './workspaces';

// Routing hash-owy (#/crm) — świadomy wybór: pozwala hostować Control Center jako statyczne
// pliki pod dowolnym prefiksem, bez konfigurowania przepisywania ścieżek na serwerze.
// Dokładnie ten sam powód, dla którego panel admina Drive używa hash-routingu.

function sectionOf(workspaceId: string, sectionId: string): WorkspaceSection {
  const ws = findWorkspace(workspaceId);
  const section = ws?.sections.find((s) => s.id === sectionId);
  if (!ws || !section) throw new Error(`Nieznana sekcja ${workspaceId}/${sectionId}`);
  return section;
}

/** Ekran zastępczy budowany Z METADANYCH nawigacji — opis blokera nie rozjeżdża się z menu. */
function Placeholder({ workspaceId, sectionId }: { workspaceId: string; sectionId: string }) {
  const section = sectionOf(workspaceId, sectionId);
  if (section.maturity === 'live') throw new Error(`Sekcja ${sectionId} jest oznaczona jako live — brak komponentu.`);
  return <NotBuiltYet title={section.label} maturity={section.maturity} note={section.note} />;
}

function guarded(workspaceId: string, sectionId: string, element: React.ReactNode) {
  const section = sectionOf(workspaceId, sectionId);
  return <RequirePermission permissions={section.permissions}>{element}</RequirePermission>;
}

export const routes: RouteObject[] = [
  {
    path: '/',
    element: <AppShell />,
    children: [
      { index: true, element: guarded('home', 'overview', <HomePage />) },

      { path: 'admin', element: guarded('admin', 'users', <Placeholder workspaceId="admin" sectionId="users" />) },
      {
        path: 'admin/schools',
        element: guarded('admin', 'schools', <Placeholder workspaceId="admin" sectionId="schools" />),
      },

      { path: 'restauracje', element: guarded('restaurants', 'sales', <RestaurantSalesPage />) },
      { path: 'restauracje/dania', element: guarded('restaurants', 'dishes', <DishSalesPage />) },
      { path: 'restauracje/szkoly', element: guarded('restaurants', 'schools', <RestaurantSchoolsPage />) },
      { path: 'restauracje/katalog', element: guarded('restaurants', 'catalog', <DishCatalogPage />) },
      {
        path: 'restauracje/menu',
        element: guarded('restaurants', 'menu', <Placeholder workspaceId="restaurants" sectionId="menu" />),
      },

      { path: 'crm', element: guarded('crm', 'school-sales', <SchoolSalesPage />) },
      { path: 'crm/transakcje', element: guarded('crm', 'transactions', <TransactionsPage />) },
      {
        path: 'crm/kampanie',
        element: guarded('crm', 'campaigns', <Placeholder workspaceId="crm" sectionId="campaigns" />),
      },

      // Przestrzeń Dostaw NIE jest bramkowana uprawnieniem: sama w sobie prosi o podłączenie
      // systemu kierowców i bez tej sesji nie pokazuje żadnych danych. Bramka jest po stronie
      // backendu Drive (requireAuth + requirePermission).
      { path: 'drive', element: <DriveOverviewPage /> },
      { path: 'drive/szkoly', element: <DriveSchoolsPage /> },
      { path: 'drive/historia', element: <DriveHistoryPage /> },

      { path: 'insights', element: guarded('insights', 'all', <InsightsPage />) },
      { path: 'insights/powiazania', element: guarded('insights', 'coincidence', <PowiazaniaPage />) },

      { path: 'raporty', element: guarded('reports', 'reports', <Placeholder workspaceId="reports" sectionId="reports" />) },

      // Status połączeń celowo BEZ bramki sesji: to jedyny ekran diagnostyczny, a nie pokazuje
      // żadnych danych domenowych — tylko konfigurację i to, czy backend odpowiada.
      { path: 'system', element: <SystemStatusPage /> },
      /* Gotowość i Jakość danych celowo BEZ strażnika uprawnień — tak samo jak status
         połączeń. Te ekrany mówią o STANIE NARZĘDZIA, a nie o danych biznesowych; osoba,
         która nie widzi sprzedaży, nadal musi móc sprawdzić, czemu jej nie widzi. */
      { path: 'system/gotowosc', element: <GotowoscPage /> },
      { path: 'system/jakosc', element: <JakoscDanychPage /> },
      { path: 'system/audyt', element: guarded('system', 'audit', <Placeholder workspaceId="system" sectionId="audit" />) },

      { path: '*', element: <Navigate to="/" replace /> },
    ],
  },
];

export const router = createHashRouter(routes);

/** Wszystkie ścieżki nawigacji — używane przez test spójności menu i routingu. */
export function navigationPaths(): string[] {
  return WORKSPACES.flatMap((w) => w.sections.map((s) => sectionPath(w, s)));
}
